/**
 * Scan POS P6 -- the Website's client for the P2-P5 Staging contracts.
 *
 *   /api/v1/scan/pair      PAIR_INVITATION_CREATE, PAIR_STATUS_WEB, PAIR_DISCONNECT_WEB, PAIR_REPLACE
 *   /api/v1/scan/surface   SURFACE_OPEN, SURFACE_STATUS, SURFACE_HEARTBEAT, SURFACE_STATE_SET,
 *                          SURFACE_TRANSFER, SURFACE_RELEASE
 *   /api/v1/scan/realtime  REALTIME_TICKET_WEB, REALTIME_STATUS_WEB
 *   /api/v1/scan/event     SCAN_ACK
 *
 * Field names are copied from `handler/scan/*` in jinda-pos-api.
 *
 * * The default axios instance is used, so the auth interceptor attaches
 *   the bearer; this module never reads or embeds a token.
 * * Every failure REJECTS (with the axios error, whose `response.data`
 *   carries the Backend's `error` code). Nothing is turned into a fake
 *   success or an empty list.
 * * Refuses outright when this build is not on the Staging API: the
 *   routes do not exist on Production yet (see realtime-config.ts).
 * * Nothing here logs a body or a response: they carry the pairing QR
 *   secret and the one-time realtime ticket.
 */
import axios from 'axios';
import { base_url } from './api-config';
import { isScanPosAvailable } from '../scan-pos/realtime-config';

export class ScanPosUnavailableError extends Error {
    constructor() {
        super('Scan POS is not available on this Backend');
        this.name = 'ScanPosUnavailableError';
    }
}

const post = async <T>(path: string, operation: string, body: Record<string, unknown> = {}): Promise<T> => {
    if (!isScanPosAvailable()) {
        throw new ScanPosUnavailableError();
    }
    const res = await axios.post(`${base_url}${path}`, JSON.stringify(body), {
        headers: { 'Content-Type': 'application/json', operation },
    });
    return res.data as T;
};

/** The Backend's refusal code (`error` field), if the failure carries one. */
export const scanErrorCode = (error: unknown): string | null => {
    const code = (error as any)?.response?.data?.error;
    return typeof code === 'string' ? code : null;
};

export const scanErrorStatus = (error: unknown): number | null => {
    const status = (error as any)?.response?.status;
    return typeof status === 'number' ? status : null;
};

// ---------------------------------------------------------------- types
export type ReceiverState = 'WAITING' | 'READY' | 'PAUSED' | 'LOCKED';
export type SurfaceRole = 'ACTIVE' | 'PASSIVE' | 'CLOSED';

export interface SurfaceView {
    surface: {
        surface_id: string;
        role: SurfaceRole;
        receiver_state: ReceiverState;
        lifecycle: string;
        closed_reason: string | null;
    } | null;
    terminal: { generation: number; active: boolean; lease_expires_at: string | null };
    lease_seconds: number;
    heartbeat_interval_seconds: number;
}

export interface WebPairView {
    pair_id: number;
    state: string;
    mobile_employee_name: string | null;
    mobile_device_label: string | null;
    created_at: string | null;
    expires_at: string | null;
    owned_by_this_session: boolean;
}

export interface Invitation {
    qr_payload: string;
    purpose: string;
    issued_at: string | null;
    expires_at: string | null;
    expires_in_seconds: number;
}

export interface PairStatus {
    terminal: { id: number; name: string };
    version: number;
    pair: WebPairView | null;
}

export interface InvitationResult extends PairStatus {
    invitation: Invitation;
}

export interface ReplaceResult {
    replaced_pair_id: number;
    version: number;
    invitation: Invitation;
}

export interface WebTicket {
    ticket: string;
    kind: string;
    expires_at: string | null;
    expires_in_seconds: number;
}

export interface AckBody {
    scan_event_id: string;
    surface_id: string;
    surface_generation: number;
    result: 'APPLIED' | 'REJECTED';
    reason?: string | null;
}

// ---------------------------------------------------------------- pair
export const pairInvitationCreate = () => post<InvitationResult>('/api/v1/scan/pair', 'PAIR_INVITATION_CREATE');
export const pairStatusWeb = () => post<PairStatus>('/api/v1/scan/pair', 'PAIR_STATUS_WEB');
export const pairDisconnectWeb = (pairId: number) =>
    post<{ ended: boolean; version: number }>('/api/v1/scan/pair', 'PAIR_DISCONNECT_WEB', { pair_id: pairId });
export const pairReplace = (pairId: number, version: number) =>
    post<ReplaceResult>('/api/v1/scan/pair', 'PAIR_REPLACE', { pair_id: pairId, version });

// ---------------------------------------------------------------- surface
export const surfaceOpen = () => post<SurfaceView>('/api/v1/scan/surface', 'SURFACE_OPEN');
export const surfaceStatus = (surfaceId: string) =>
    post<SurfaceView>('/api/v1/scan/surface', 'SURFACE_STATUS', { surface_id: surfaceId });
export const surfaceHeartbeat = (surfaceId: string, generation: number) =>
    post<SurfaceView>('/api/v1/scan/surface', 'SURFACE_HEARTBEAT', { surface_id: surfaceId, generation });
export const surfaceStateSet = (surfaceId: string, state: ReceiverState, generation: number) =>
    post<SurfaceView>('/api/v1/scan/surface', 'SURFACE_STATE_SET', { surface_id: surfaceId, state, generation });
export const surfaceTransfer = (surfaceId: string, generation: number) =>
    post<SurfaceView>('/api/v1/scan/surface', 'SURFACE_TRANSFER', { surface_id: surfaceId, generation });
export const surfaceRelease = (surfaceId: string) =>
    post<SurfaceView & { released: boolean }>('/api/v1/scan/surface', 'SURFACE_RELEASE', { surface_id: surfaceId });

// ---------------------------------------------------------------- realtime
export const realtimeTicketWeb = (surfaceId: string, generation: number) =>
    post<WebTicket>('/api/v1/scan/realtime', 'REALTIME_TICKET_WEB', { surface_id: surfaceId, generation });
export const realtimeStatusWeb = (surfaceId: string) =>
    post<{ connected: boolean }>('/api/v1/scan/realtime', 'REALTIME_STATUS_WEB', { surface_id: surfaceId });

// ---------------------------------------------------------------- event
export const scanAck = (body: AckBody) => post<Record<string, unknown>>('/api/v1/scan/event', 'SCAN_ACK', { ...body });

/** Everything the Surface session controller needs, injectable for tests. */
export const scanPosApi = {
    surfaceOpen, surfaceStatus, surfaceHeartbeat, surfaceStateSet, surfaceTransfer, surfaceRelease,
    realtimeTicketWeb, scanAck,
};
export type ScanPosApi = typeof scanPosApi;
